/**
 * Bridge Migration Management - Prime artifact target + prime artifact mapping.
 * Step 1: Select target prime artifact (parent for migrated bridge sessions - abstracted away).
 * Step 2: Map required connector keys to production prime artifacts.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, List, Select, Button, message, Spin, Alert, Space } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { apiClient } from '../services/apiClient';
import { localBridgeApiClient } from '../services/localBridgeApiClient';
import type { PrimeArtifact } from '../types/primeArtifact';

const { Option } = Select;

const PRIME_ARTIFACTS_LIMIT = 1000;

/** Convert mapping key to search-friendly label (e.g. business_domain -> business domain) */
function keyToSearchLabel(key: string): string {
  return key.replace(/_/g, ' ');
}

/** Extract a displayable string from API error response (handles Pydantic validation errors) */
function getErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { detail?: string | unknown[] | Record<string, unknown> } }; message?: string };
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d: unknown) => {
        const item = d as { msg?: string };
        return typeof item.msg === 'string' ? item.msg : JSON.stringify(d);
      })
      .join('; ') || 'Validation error';
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return e?.message || 'Failed to load data';
}

interface PrimeArtifactMatch {
  production_prime_artifact_id: string;
  production_prime_artifact_name: string;
}

interface RequiredMappingKeys {
  mapping_keys: string[];
  hierarchy: Record<string, string[]>;
}

interface PrimeArtifactMatchEntry {
  required_mapping_key: string;
  production_prime_artifact_id: string;
  production_prime_artifact_name: string;
}

export const BridgeMigrationPage: React.FC = () => {
  const [primeArtifacts, setPrimeArtifacts] = useState<PrimeArtifact[]>([]);
  const [required, setRequired] = useState<RequiredMappingKeys | null>(null);
  const [primeMatch, setPrimeMatch] = useState<PrimeArtifactMatch | null>(null);
  const [matches, setMatches] = useState<Record<string, PrimeArtifactMatchEntry>>({});
  const [searchValues, setSearchValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localBridgeError, setLocalBridgeError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const sortedPrimeArtifacts = useMemo(() => {
    return [...primeArtifacts].sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
    );
  }, [primeArtifacts]);

  // Include matched prime artifacts in options even if not in fetched list (avoids Select label mismatch)
  const displayPrimeArtifacts = useMemo(() => {
    const byId = new Map<string, PrimeArtifact>();
    sortedPrimeArtifacts.forEach((a) => byId.set(a.id, a));
    const addIfMissing = (id: string, name: string) => {
      if (!byId.has(id)) {
        byId.set(id, { id, name } as PrimeArtifact);
      }
    };
    if (primeMatch) addIfMissing(primeMatch.production_prime_artifact_id, primeMatch.production_prime_artifact_name);
    Object.values(matches).forEach((m) => addIfMissing(m.production_prime_artifact_id, m.production_prime_artifact_name));
    return Array.from(byId.values()).sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
    );
  }, [sortedPrimeArtifacts, primeMatch, matches]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLocalBridgeError(null);

    try {
      // Production: prime artifacts (fetch more than default 100, sorted client-side)
      const primeRes = await apiClient.get<PrimeArtifact[]>('/prime-artifacts', {
        params: { skip: 0, limit: PRIME_ARTIFACTS_LIMIT },
      });
      const raw = Array.isArray(primeRes.data) ? primeRes.data : [];
      setPrimeArtifacts(raw);

      // Local bridge: required mapping keys, prime target match, prime artifact matches
      if (localBridgeApiClient) {
        const [reqRes, primeMatchRes, matchRes] = await Promise.all([
          localBridgeApiClient.get<RequiredMappingKeys>('/migration/required-prime-artifact-mapping-keys'),
          localBridgeApiClient.get<{ match: PrimeArtifactMatch | null }>('/migration/prime-artifact-match'),
          localBridgeApiClient.get<{ matches: PrimeArtifactMatchEntry[] }>('/migration/prime-artifact-matches'),
        ]);
        setRequired(reqRes.data);
        setPrimeMatch(primeMatchRes.data?.match ?? null);
        const matchMap: Record<string, PrimeArtifactMatchEntry> = {};
        (matchRes.data.matches || []).forEach((m) => {
          matchMap[m.required_mapping_key] = m;
        });
        setMatches(matchMap);
      } else {
        setLocalBridgeError('Local Bridge API is not available. Ensure the backend is running.');
      }
    } catch (e: unknown) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handlePrimeMatch = async (primeArtifactId: string) => {
    const prime = displayPrimeArtifacts.find((a) => a.id === primeArtifactId);
    if (!prime || !localBridgeApiClient) return;
    setSaving(true);
    try {
      await localBridgeApiClient.put('/migration/prime-artifact-match', {
        production_prime_artifact_id: prime.id,
        production_prime_artifact_name: prime.name,
      });
      setPrimeMatch({
        production_prime_artifact_id: prime.id,
        production_prime_artifact_name: prime.name,
      });
      message.success(`Matched to ${prime.name}`);
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handlePrimeUnmatch = async () => {
    if (!localBridgeApiClient) return;
    setSaving(true);
    try {
      await localBridgeApiClient.delete('/migration/prime-artifact-match');
      setPrimeMatch(null);
      message.success('Match removed');
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleMatch = async (requiredKey: string, primeArtifactId: string) => {
    const prime = displayPrimeArtifacts.find((a) => a.id === primeArtifactId);
    if (!prime || !localBridgeApiClient) return;
    setSaving(true);
    try {
      await localBridgeApiClient.post('/migration/prime-artifact-matches', {
        required_mapping_key: requiredKey,
        production_prime_artifact_id: prime.id,
        production_prime_artifact_name: prime.name,
      });
      setMatches((prev) => ({
        ...prev,
        [requiredKey]: {
          required_mapping_key: requiredKey,
          production_prime_artifact_id: prime.id,
          production_prime_artifact_name: prime.name,
        },
      }));
      message.success(`Matched ${requiredKey} to ${prime.name}`);
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleUnmatch = async (requiredKey: string) => {
    if (!localBridgeApiClient) return;
    setSaving(true);
    try {
      await localBridgeApiClient.delete(`/migration/prime-artifact-matches/${requiredKey}`);
      setMatches((prev) => {
        const next = { ...prev };
        delete next[requiredKey];
        return next;
      });
      message.success('Match removed');
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const mappingKeys = required?.mapping_keys ?? [];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <h1>Bridge Migration</h1>
      <p style={{ color: '#666', marginBottom: 24 }}>
        Step 1: Select the target prime artifact (parent for migrated bridge sessions). Step 2: Map
        each connector key to a production prime artifact.
      </p>

      {localBridgeError && (
        <Alert type="warning" message={localBridgeError} style={{ marginBottom: 16 }} />
      )}
      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Card title="Step 1: Prime Artifact (Target)" style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Target prime artifact (abstracted away in migration)
            </label>
            <Space.Compact style={{ width: '100%', maxWidth: 480 }}>
              <Select
                placeholder="Select prime artifact..."
                value={primeMatch?.production_prime_artifact_id ?? undefined}
                onChange={(id) => handlePrimeMatch(id)}
                style={{ flex: 1, minWidth: 280 }}
                disabled={saving || !localBridgeApiClient}
                allowClear
                onClear={handlePrimeUnmatch}
                showSearch
                optionFilterProp="label"
                filterOption={(input, opt) => {
                  const id = opt?.value as string;
                  const artifact = displayPrimeArtifacts.find((a) => a.id === id);
                  const label = artifact ? `${artifact.name} ${artifact.artifact_type_name || ''}` : '';
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {displayPrimeArtifacts.map((a) => (
                  <Option
                    key={a.id}
                    value={a.id}
                    label={`${a.name}${a.artifact_type_name ? ` (${a.artifact_type_name})` : ''}`}
                  >
                    {a.name}
                    {a.artifact_type_name && ` (${a.artifact_type_name})`}
                  </Option>
                ))}
              </Select>
              {primeMatch && (
                <Button
                  type="default"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handlePrimeUnmatch}
                  disabled={saving}
                >
                  Clear
                </Button>
              )}
            </Space.Compact>
          </div>
          {primeMatch && (
            <div style={{ color: '#52c41a', fontSize: 14 }}>
              Migrated bridge sessions will be created under:{' '}
              <strong>{primeMatch.production_prime_artifact_name}</strong>
            </div>
          )}
        </Space>
      </Card>

      <Card title="Step 2: Prime Artifact Mapping (Connector References)" style={{ marginBottom: 24 }}>
        <p style={{ color: '#666', marginBottom: 16, fontSize: 13 }}>
          Map each required connector key to a production prime artifact. Prime artifacts should
          already exist.
        </p>
        <List
          dataSource={mappingKeys}
          renderItem={(key) => {
            const match = matches[key];
            const defaultSearch = keyToSearchLabel(key);
            return (
              <List.Item
                key={key}
                actions={[
                  match && (
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleUnmatch(key)}
                      disabled={saving}
                    >
                      Unmatch
                    </Button>
                  ),
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <span>{key}</span>
                      {match && (
                        <span style={{ color: '#52c41a', fontSize: 12 }}>
                          → {match.production_prime_artifact_name}
                        </span>
                      )}
                    </Space>
                  }
                />
                <Select
                  placeholder="Match to prime artifact..."
                  value={match?.production_prime_artifact_id ?? undefined}
                  onChange={(id) => handleMatch(key, id)}
                  style={{ width: 320 }}
                  disabled={saving || !localBridgeApiClient}
                  allowClear
                  onClear={() => handleUnmatch(key)}
                  showSearch
                  searchValue={searchValues[key] ?? ''}
                  onSearch={(v) => setSearchValues((prev) => ({ ...prev, [key]: v }))}
                  onDropdownVisibleChange={(open) => {
                    if (open) {
                      setSearchValues((prev) => ({ ...prev, [key]: defaultSearch }));
                    } else {
                      setSearchValues((prev) => {
                        const next = { ...prev };
                        delete next[key];
                        return next;
                      });
                    }
                  }}
                  optionFilterProp="label"
                  filterOption={(input, opt) => {
                    const id = opt?.value as string;
                    const artifact = displayPrimeArtifacts.find((a) => a.id === id);
                    const label = artifact ? `${artifact.name} ${artifact.artifact_type_name || ''}` : '';
                    return label.toLowerCase().includes((input || '').toLowerCase());
                  }}
                >
                  {displayPrimeArtifacts.map((a) => (
                    <Option
                      key={a.id}
                      value={a.id}
                      label={`${a.name}${a.artifact_type_name ? ` (${a.artifact_type_name})` : ''}`}
                    >
                      {a.name}
                      {a.artifact_type_name && ` (${a.artifact_type_name})`}
                    </Option>
                  ))}
                </Select>
              </List.Item>
            );
          }}
        />
      </Card>
    </div>
  );
};
